import { FC } from 'react'

import styled from 'styled-components'
import { Controller, useForm } from 'react-hook-form'

import { AddEquipmentBody, DriveTypeEnum } from 'src/Redux/api/equipmentApi.ts'
import { Input } from 'src/Components/Input/index.tsx'
import { Select } from 'src/Components/Select'
import { Button } from 'src/Components/Button'
import { Model } from 'src/Redux/api/modelApi.ts'
import { colors, device } from 'src/Helpers/constants.ts'
import { Textarea } from 'src/Components/Textarea/index.tsx'
import { Checkbox } from 'src/Components/Checkbox/index.tsx'
import { HintTooltip } from 'src/Components/HintTooltip'
import { useIsAdmin } from 'src/Hooks/useIsAdmin.ts'
import { FileInput } from 'src/Components/FileInput'
import { Modal } from 'src/Components/Modal'
import { ModelType } from 'src/Redux/api/modelTypesApi.ts'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: center;

  @media ${device.tabletMax} {
    flex-direction: column;
  }
`

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 300px;
`

const RightRow = styled(RowContainer)``

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.secondGray};
`

const VerticalSeparator = styled.div`
  width: 1px;
  height: 700px;
  background-color: ${colors.secondGray};

  @media ${device.tabletMax} {
    display: none;
  }
`

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const ModalContainer = styled.div`
  padding-top: 45px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Text = styled.span`
  font-size: 14px;
  max-width: 200px;
  width: 100%;
  text-align: left;
  color: ${colors.thirdGray};
  font-weight: 600;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`

export interface AddEquipmentForm {
  year: number
  registrationNumber?: string
  engineNumber?: string
  color?: string
  vin?: string
  passportNumber?: string
  estValue?: number
  isOwned?: boolean
  modelId?: string
  name?: string
  type?: string
  driveType?: DriveTypeEnum
  baseRentalCost?: number
  fuelEfficiencySummer?: number
  fuelEfficiencyWinter?: number
  engineCapacity?: number
  description?: string
  image?: File
  operationalLogs?: string
}

interface AddTransportModalProps {
  onClose: () => void
  onAddEquipment: (equipment: AddEquipmentForm) => void
  isOpened: boolean
  allModels?: Model[]
  modelTypes?: ModelType[]
}

export const AddTransportModal: FC<AddTransportModalProps> = ({
  isOpened,
  onClose,
  onAddEquipment,
  allModels,
  modelTypes,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<AddEquipmentForm>({ defaultValues: { isOwned: true } })
  const isModelSelected = Boolean(watch('modelId'))
  const isAdmin = useIsAdmin()

  const onSubmit = (data: AddEquipmentBody): void => {
    onAddEquipment(data)
    reset()
    onClose()
  }

  const typeSelectData = modelTypes?.map((type) => {
    return {
      value: String(type.id),
      label: type.label,
    }
  })

  const driveTypeSelectData = [
    { value: DriveTypeEnum.CRAWLER, label: 'Гусеничный' },
    { value: DriveTypeEnum.WHEEL, label: 'Колесный' },
  ]

  const modelsForSelect =
    allModels?.map((model) => {
      return {
        value: String(model.id),
        label: `${model.modelType.label} ${model.name}`,
      }
    }) || []

  return (
    <Modal opened={isOpened} onClose={onClose} title='Добавить технику'>
      <ModalContainer>
        <Container>
          <RowContainer>
            <Text>Модель</Text>
            <Text>
              Выберите модель из списка, либо введите данные для создания новой
              модели
            </Text>

            <Controller
              name='modelId'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Модель'
                  label='Модель'
                  data={modelsForSelect}
                  error={errors.type?.message}
                  clearable={true}
                  searchable
                  nothingFoundMessage='Ничего не найдено'
                  {...field}
                  disabled={!modelsForSelect || modelsForSelect.length === 0}
                />
              )}
            />

            <Separator />

            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Наименование'
                  label='Наименование'
                  disabled={isModelSelected}
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='type'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Тип техники'
                  label='Тип техники'
                  disabled={isModelSelected}
                  data={typeSelectData}
                  error={errors.type?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='driveType'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Вид движителя'
                  label='Вид движителя'
                  disabled={isModelSelected}
                  data={driveTypeSelectData}
                  error={errors.driveType?.message}
                  {...field}
                />
              )}
            />

            <HintTooltip
              label='Изменять стоимость аренды может только администратор'
              disabled={isAdmin}
            >
              <Controller
                name='baseRentalCost'
                control={control}
                render={({ field }) => (
                  <Input
                    placeholder='Базовая стоимость аренды, руб./ч'
                    disabled={isModelSelected || !isAdmin}
                    type='number'
                    label='Базовая стоимость аренды'
                    error={errors.baseRentalCost?.message}
                    {...field}
                    value={field.value?.toString() || ''}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || '')
                    }
                  />
                )}
              />
            </HintTooltip>

            <Controller
              name='fuelEfficiencySummer'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Расход топлива (лето), л/ч'
                  disabled={isModelSelected}
                  type='number'
                  label='Расход топлива (лето)'
                  error={errors.fuelEfficiencySummer?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || '')
                  }
                />
              )}
            />

            <Controller
              name='fuelEfficiencyWinter'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Расход топлива (зима), л/ч'
                  disabled={isModelSelected}
                  type='number'
                  label='Расход топлива (зима)'
                  error={errors.fuelEfficiencyWinter?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || '')
                  }
                />
              )}
            />

            <Controller
              name='engineCapacity'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Объем двигателя, куб. см'
                  disabled={isModelSelected}
                  type='number'
                  label='Объем двигателя'
                  error={errors.engineCapacity?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || '')
                  }
                />
              )}
            />

            <Controller
              name='operationalLogs'
              control={control}
              render={({ field }) => (
                <Select
                  placeholder='Вид первичных учетных документов'
                  label='Вид первичных учетных документов'
                  disabled={isModelSelected}
                  data={[
                    { value: 'Путевой лист', label: 'Путевой лист' },
                    { value: 'ЭСМ3', label: 'ЭСМ3' },
                  ]}
                  error={errors.operationalLogs?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <Textarea
                  placeholder='Описание'
                  disabled={isModelSelected}
                  label='Описание'
                  minRows={3}
                  autosize
                  {...field}
                />
              )}
            />

            <Controller
              name='image'
              control={control}
              render={({ field: { onChange, ...field } }) => (
                <FileInput
                  label='Фото техники'
                  placeholder='Выберите фото'
                  accept='image/*'
                  disabled={isModelSelected}
                  onChange={onChange}
                  {...field}
                />
              )}
            />
          </RowContainer>
          <VerticalSeparator />
          <RightRow>
            <TextWrapper>
              <Text>Экземпляр модели ТС</Text>
              <Text>Выберите данные экземпляра транспортного средства</Text>
            </TextWrapper>

            <Controller
              name='year'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Год выпуска'
                  label='Год выпуска'
                  type='number'
                  error={errors.year?.message}
                  {...field}
                  value={field.value?.toString()}
                  onChange={(e) =>
                    field.onChange(parseInt(e.target.value) || '')
                  }
                />
              )}
            />

            <Controller
              name='registrationNumber'
              control={control}
              render={({ field }) => (
                <Input
                  label='Регистрационный номер'
                  placeholder='Регистрационный номер'
                  error={errors.registrationNumber?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='engineNumber'
              control={control}
              render={({ field }) => (
                <Input
                  label='Номер двигателя'
                  placeholder='Номер двигателя'
                  error={errors.engineNumber?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='color'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Цвет'
                  label='Цвет'
                  error={errors.color?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='vin'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='VIN номер'
                  label='VIN номер'
                  error={errors.vin?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='passportNumber'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Номер паспорта ТС'
                  label='Номер паспорта ТС'
                  error={errors.passportNumber?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name='estValue'
              control={control}
              render={({ field }) => (
                <Input
                  placeholder='Оценочная стоимость, руб.'
                  label='Оценочная стоимость'
                  type='number'
                  error={errors.estValue?.message}
                  {...field}
                  value={field.value?.toString() || ''}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || '')
                  }
                />
              )}
            />

            <Controller
              name='isOwned'
              control={control}
              render={({ field: { value, onChange } }) => (
                <HintTooltip label='Снимите, если техника заведена только для выставления КП и не входит в ваш парк'>
                  <Checkbox
                    label='В собственности'
                    checked={value ?? true}
                    onChange={(event) => onChange(event.currentTarget.checked)}
                    data-testid='equipment-is-owned'
                  />
                </HintTooltip>
              )}
            />
          </RightRow>
        </Container>
        <ButtonsWrapper>
          <Button onClick={handleSubmit(onSubmit)}>Добавить</Button>
        </ButtonsWrapper>
      </ModalContainer>
    </Modal>
  )
}
